import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSEO } from '../hooks/useSEO';

const PrivacyPolicy = () => {
  useSEO({
    title: "Privacy Policy | AM CARDMATE",
    description: "Read AM CARDMATE's privacy policy to understand how we collect, use, and protect your personal information and data.",
    image: "/assets/account.jpg",
    keywords: "privacy policy, data protection, privacy, GDPR, data security"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Privacy Policy",
        "description": "AM CARDMATE's privacy policy explaining data collection, usage, and protection",
        "url": "https://amcardmate.com/privacy",
        "dateModified": "2025-01-15",
        "publisher": {
          "@type": "Organization",
          "name": "AM CARDMATE"
        },
        "inLanguage": "en-GB",
        "isPartOf": {
          "@type": "WebSite",
          "name": "AM CARDMATE",
          "url": "https://amcardmate.com"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "AM CARDMATE Privacy Policy",
        "datePublished": "2025-01-01",
        "dateModified": "2025-01-15",
        "author": {
          "@type": "Organization",
          "name": "AM CARDMATE Legal Team"
        },
        "publisher": {
          "@type": "Organization",
          "name": "AM CARDMATE",
          "logo": {
            "@type": "ImageObject",
            "url": "https://amcardmate.com/logo.png"
          }
        }
      }
    ];
    
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const [activeSection, setActiveSection] = useState(0);
  
  const sections = [
    {
      title: 'Information We Collect',
      icon: 'database',
      content: [
        {
          subtitle: 'Personal Information',
          items: [
            'Name, email address, and phone number',
            'Profile information you add to your digital cards',
            'Social media links you choose to include',
            'Company information and job title'
          ]
        },
        {
          subtitle: 'Usage Information',
          items: [
            'Card creation and sharing activities',
            'Analytics data (for premium users)',
            'Device information and IP address',
            'App usage patterns and preferences'
          ]
        },
        {
          subtitle: 'Payment Information',
          items: [
            'Subscription details (processed by Apple/Google)',
            'Purchase history',
            'We do NOT store credit card information'
          ]
        }
      ]
    },
    {
      title: 'How We Use Your Information',
      icon: 'cogs',
      content: [
        {
          subtitle: 'To Provide Services',
          items: [
            'Create and manage your digital business cards',
            'Enable sharing features (NFC, QR, links)',
            'Process subscriptions and payments',
            'Provide customer support'
          ]
        },
        {
          subtitle: 'To Improve Our App',
          items: [
            'Analyze usage patterns to enhance features',
            'Fix bugs and improve performance',
            'Develop new functionalities',
            'Personalize your experience'
          ]
        }
      ]
    },
    {
      title: 'Data Sharing & Disclosure',
      icon: 'share-alt',
      content: [
        {
          subtitle: 'We Never Sell Your Data',
          items: [
            'Your personal information is never sold to third parties',
            'We do not share data with advertisers',
            'No data mining or profiling for marketing'
          ]
        },
        {
          subtitle: 'Limited Sharing',
          items: [
            'With your explicit consent only',
            'To comply with legal obligations',
            'To protect our rights and prevent fraud',
            'With service providers under strict agreements'
          ]
        }
      ]
    },
    {
      title: 'Data Security',
      icon: 'shield-alt',
      content: [
        {
          subtitle: 'Protection Measures',
          items: [
            'End-to-end encryption for sensitive data',
            'Secure servers with regular security audits',
            'Limited access to personal information',
            'Regular security updates and patches'
          ]
        },
        {
          subtitle: 'Your Security',
          items: [
            'Use strong passwords for your account',
            'Keep your app updated to the latest version',
            'Be cautious when sharing on public networks',
            'Report any suspicious activity immediately'
          ]
        }
      ]
    },
    {
      title: 'Your Rights',
      icon: 'user-shield',
      content: [
        {
          subtitle: 'You Have the Right To',
          items: [
            'Access your personal data anytime',
            'Correct or update your information',
            'Delete your account and all associated data',
            'Export your data in a portable format',
            'Opt-out of analytics and communications',
            'Lodge a complaint with supervisory authorities'
          ]
        }
      ]
    },
    {
      title: 'International Transfers',
      icon: 'globe',
      content: [
        {
          subtitle: 'Data Location',
          items: [
            'Data is stored on secure servers in the UK',
            'We comply with GDPR requirements',
            'International transfers use appropriate safeguards',
            'You can request data location information'
          ]
        }
      ]
    }
  ];

  const privacyHighlights = [
    { icon: 'lock', text: 'End-to-End Encryption', color: 'from-green-500 to-emerald-600' },
    { icon: 'ban', text: 'We Never Sell Data', color: 'from-red-500 to-pink-600' },
    { icon: 'shield-alt', text: 'GDPR Compliant', color: 'from-blue-500 to-indigo-600' },
    { icon: 'user-check', text: 'You Control Your Data', color: 'from-purple-500 to-pink-600' }
  ];

  return (
    <>
      <section className="pt-20 md:pt-24 lg:pt-32 pb-20 bg-gradient-to-b from-white via-blue-50/30 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="badge badge-green inline-flex mb-6">
              <FontAwesomeIcon icon="shield-alt" />
              <span>Privacy First</span>
            </div>
            
            <h1 className="text-h1 mb-6">
              Your Privacy <span className="text-gradient-blue-pink">Matters</span>
            </h1>
            
            <p className="text-body-lg text-gray max-w-3xl mx-auto mb-8">
              At AM CARDMATE, we're committed to protecting your personal information and being transparent about how we use it.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm">
                <FontAwesomeIcon icon="calendar" className="text-blue-500" />
                <span className="text-body-sm"><strong>Last Updated:</strong> January 15, 2025</span>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm">
                <FontAwesomeIcon icon="check-circle" className="text-green-500" />
                <span className="text-body-sm"><strong>Status:</strong> GDPR Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {privacyHighlights.map((highlight, index) => (
                <div key={index} className="text-center">
                  <div className={`w-16 h-16 bg-gradient-to-br ${highlight.color} rounded-2xl flex items-center justify-center text-white mx-auto mb-3 shadow-lg`}>
                    <FontAwesomeIcon icon={highlight.icon} className="text-2xl" />
                  </div>
                  <p className="text-body-sm font-medium text-dark">{highlight.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-1">
                <div className="sticky top-24 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-h5 text-dark mb-4">Privacy Topics</h3>
                  <nav className="space-y-2">
                    {sections.map((section, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveSection(index)}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-all ${
                          activeSection === index 
                            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' 
                            : 'hover:bg-gray-100 text-gray-600'
                        }`}
                      >
                        <FontAwesomeIcon icon={section.icon} className="mr-2 text-sm" />
                        <span className="text-body-sm">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                  
                  <div className="mt-8 p-4 bg-green-50 rounded-xl">
                    <FontAwesomeIcon icon="shield-alt" className="text-green-600 text-2xl mb-2" />
                    <p className="text-body-sm font-medium text-green-800">Your data is safe</p>
                    <p className="text-caption text-green-600 mt-1">256-bit encryption</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3 space-y-8">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8">
                  <h2 className="text-h3 text-dark mb-4">Our Privacy Commitment</h2>
                  <p className="text-body text-gray">
                    AM CARDMATE respects your privacy and is committed to protecting your personal data. 
                    This privacy policy explains how we collect, use, and safeguard your information. 
                    We believe in complete transparency and giving you control over your data.
                  </p>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-lg">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-600 rounded-2xl flex items-center justify-center text-white">
                      <FontAwesomeIcon icon={sections[activeSection].icon} className="text-2xl" />
                    </div>
                    <h2 className="text-h3 text-dark">{sections[activeSection].title}</h2>
                  </div>
                  
                  <div className="space-y-8">
                    {sections[activeSection].content.map((subsection, subIndex) => (
                      <div key={subIndex}>
                        <h3 className="text-h4 text-dark mb-4">{subsection.subtitle}</h3>
                        <ul className="space-y-3">
                          {subsection.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-3">
                              <FontAwesomeIcon icon="check-circle" className="text-green-500 mt-1 flex-shrink-0" />
                              <span className="text-body text-gray">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between items-center pt-8">
                  <button
                    onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                    className={`btn btn-outline ${activeSection === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={activeSection === 0}
                  >
                    <FontAwesomeIcon icon="arrow-left" />
                    <span>Previous</span>
                  </button>
                  
                  <div className="flex gap-2">
                    {sections.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveSection(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          activeSection === index 
                            ? 'w-8 bg-gradient-to-r from-blue-500 to-purple-600' 
                            : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setActiveSection(Math.min(sections.length - 1, activeSection + 1))}
                    className={`btn btn-outline ${activeSection === sections.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={activeSection === sections.length - 1}
                  >
                    <span>Next</span>
                    <FontAwesomeIcon icon="arrow-right" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto text-center">
            <div className="badge badge-purple inline-flex mb-6">
              <FontAwesomeIcon icon="user-shield" />
              <span>Your Rights</span>
            </div>
            
            <h2 className="text-h2 mb-12">
              You're in <span className="text-gradient-blue-pink">Complete Control</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: 'download', title: 'Export Data', desc: 'Download all your data anytime' },
                { icon: 'edit', title: 'Update Info', desc: 'Change your details instantly' },
                { icon: 'trash-alt', title: 'Delete Account', desc: 'Remove all data permanently' },
                { icon: 'eye-slash', title: 'Privacy Controls', desc: 'Choose what to share' },
                { icon: 'ban', title: 'Opt Out', desc: 'Disable analytics & tracking' },
                { icon: 'gavel', title: 'Legal Rights', desc: 'Exercise your GDPR rights' }
              ].map((right, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-600 rounded-xl flex items-center justify-center text-white mx-auto mb-4">
                    <FontAwesomeIcon icon={right.icon} className="text-xl" />
                  </div>
                  <h3 className="text-h5 text-dark mb-2">{right.title}</h3>
                  <p className="text-body-sm text-gray">{right.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-blue-pink text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-15"></div>
        <div className="absolute top-20 left-10 w-24 h-24 rounded-full bg-white opacity-20"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-white opacity-10"></div>
        <div className="absolute top-1/3 right-1/4 w-16 h-16 rounded-full bg-white opacity-15"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-h2 mb-6">Privacy Questions?</h2>
            <p className="text-body-lg text-white/90 mb-12">
              Our team is here to help with any privacy concerns.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <a 
                href="mailto:support@amcardmate.com"
                className="bg-white/20 backdrop-blur-sm rounded-xl p-6 hover:bg-white/30 transition-all flex items-center gap-4"
              >
                <FontAwesomeIcon icon="envelope" className="text-3xl" />
                <div className="text-left">
                  <p className="text-h5">Email Us</p>
                  <p className="text-body-sm text-white/80">support@amcardmate.com</p>
                </div>
              </a>
              
              <a 
                href="mailto:support@amcardmate.com"
                className="bg-white/20 backdrop-blur-sm rounded-xl p-6 hover:bg-white/30 transition-all flex items-center gap-4"
              >
                <FontAwesomeIcon icon="user-shield" className="text-3xl" />
                <div className="text-left">
                  <p className="text-h5">Data Protection</p>
                  <p className="text-body-sm text-white/80">support@amcardmate.com</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;